import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, FolderKanban } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useProjetos, useProjetoMutations } from "@/features/projetos/hooks/useProjetos";
import { useUsuarios } from "@/features/usuarios/hooks/useUsuarios";
import { STATUS_PROJETO } from "@/features/projetos/types";

const STATUS_CLASSE: Record<number, string> = {
  0: "bg-slate-100 text-slate-700",
  1: "bg-blue-100 text-blue-700",
  2: "bg-amber-100 text-amber-700",
  3: "bg-green-100 text-green-700",
  4: "bg-red-100 text-red-700",
};

const fmtData = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const fmtMoeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Projetos() {
  const navigate = useNavigate();
  const { data: projetos = [], isLoading } = useProjetos();
  const { excluir } = useProjetoMutations();
  const { query: usuariosQuery } = useUsuarios();
  const usuarios = usuariosQuery.data?.itens ?? [];
  const nomeUsuario = (id: number) => usuarios.find(u => u.id === id)?.nome ?? (id ? `#${id}` : "—");

  const [busca, setBusca] = useState("");
  const [excluirId, setExcluirId] = useState<number | null>(null);

  const filtrados = busca
    ? projetos.filter(p => p.nome.toLowerCase().includes(busca.toLowerCase()))
    : projetos;

  const confirmarExclusao = async () => {
    if (excluirId == null) return;
    try {
      await excluir.mutateAsync(excluirId);
      toast.success("Projeto excluído.");
    } catch (e) {
      toast.error("Não foi possível excluir o projeto.");
    } finally {
      setExcluirId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <FolderKanban className="h-6 w-6" /> Projetos
          </h1>
          <p className="text-sm text-muted-foreground">Gestão de projetos, fases, tarefas e dependências.</p>
        </div>
        <Button onClick={() => navigate("/operacional/projetos/novo")}>
          <Plus className="mr-2 h-4 w-4" /> Novo Projeto
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Input
            placeholder="Buscar por nome..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="mb-4 max-w-sm"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Gerente</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[180px]">Progresso</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Fim</TableHead>
                <TableHead className="text-right">Valor orçado</TableHead>
                <TableHead className="w-[100px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : filtrados.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Nenhum projeto cadastrado.</TableCell></TableRow>
              ) : (
                filtrados.map(p => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/operacional/projetos/${p.id}`)}>
                    <TableCell className="font-medium">{p.nome}</TableCell>
                    <TableCell>{nomeUsuario(p.gerenteId)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_CLASSE[p.status] ?? ""}>
                        {STATUS_PROJETO[p.status] ?? p.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={Number(p.percentualConcluido)} className="h-2" />
                        <span className="w-12 text-right text-xs tabular-nums">{Number(p.percentualConcluido).toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{fmtData(p.dtInicioPlanejada)}</TableCell>
                    <TableCell>{fmtData(p.dtFimPlanejada)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoeda(Number(p.valorOrcado))}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/operacional/projetos/${p.id}`)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setExcluirId(p.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={excluirId != null} onOpenChange={o => !o && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              O projeto será excluído com todas as suas fases, tarefas e dependências. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusao}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
